import React, { useState, useCallback, useRef, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiMagnify, mdiAccountPlus, mdiClose, mdiPaw, mdiAccountCircleOutline, mdiCheckCircle, mdiCircleOutline } from "@mdi/js";
import { toast } from "react-toastify";
import { classApi } from "../../api/classes";
import CustomFormInput from "../forms/CustomFormInput";
import "./AddStudentModal.css";

/**
 * AddStudentModal - Enrol AND check in a student in tonight's class
 * Step 1 (Who): pick "Existing student" (search) or "New to school" (register).
 * Step 2 (Why): pick why they're attending tonight — normal enrolment / walk-in / make-up / trial.
 * The attendance type is stored on the attendance record — not on the enrolment itself.
 */
const WhoToggle = ({ who, onChange }) => (
    <div className="add-student-who-toggle">
        <button
            type="button"
            className={`add-student-who-btn${who === "existing" ? " active" : ""}`}
            onClick={() => onChange("existing")}>
            Existing student
        </button>
        <button
            type="button"
            className={`add-student-who-btn${who === "new" ? " active" : ""}`}
            onClick={() => onChange("new")}>
            New to school
        </button>
    </div>
);

const RoleToggle = ({ role, onChange }) => (
    <div className="add-student-role-toggle">
        <button
            type="button"
            className={`add-student-role-btn${role === "leader" ? " active-leader" : ""}`}
            onClick={() => onChange(role === "leader" ? null : "leader")}>
            Leader
        </button>
        <button
            type="button"
            className={`add-student-role-btn${role === null ? " active-unspecified" : ""}`}
            onClick={() => onChange(null)}>
            Not specified
        </button>
        <button
            type="button"
            className={`add-student-role-btn${role === "follower" ? " active-follower" : ""}`}
            onClick={() => onChange(role === "follower" ? null : "follower")}>
            Follower
        </button>
    </div>
);

const AddStudentModal = ({ classId, classIds, termId, shortCourseId, enrolledStudentIds = new Set(), isPastDay = false, defaultDate, onClose, onStudentAdded }) => {
    const [who, setWho] = useState("existing"); // 'existing' | 'new'
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [sendInvite, setSendInvite] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [role, setRole] = useState(null);
    const [attendanceType, setAttendanceType] = useState("normal"); // 'normal' | 'walkin' | 'makeup' | 'trial'
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [trialDeals, setTrialDeals] = useState([]);
    const [trialDealId, setTrialDealId] = useState("");
    const [checkInAfterEnrol, setCheckInAfterEnrol] = useState(!isPastDay);

    useEffect(() => {
        if (attendanceType !== "trial") return;
        const filters = { status: "active" };
        if (classId) filters.eligibleClassId = classId;
        classApi.getTrialDeals(filters).then(r => {
            if (r.ok) setTrialDeals(r.data.trialDeals || []);
        }).catch(() => {});
    }, [attendanceType, classId]);

    const isAlreadyEnrolled = student => enrolledStudentIds.has(student.id);

    // Build enrolments array from classId (single), classIds (multi), or shortCourseId
    const getEnrolments = () => {
        if (shortCourseId) {
            return [{ shortCourseId, type: "short-course", role }];
        }
        if (classIds && classIds.length > 0) {
            return classIds.map(id => ({ classId: id, type: "term-class", role }));
        }
        return [{ classId, type: "term-class", role }];
    };

    const handleSearch = useCallback(async q => {
        setSearchQuery(q);
        setSelectedStudent(null);
        if (q.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const response = await classApi.searchStudents(q);
            if (response.ok) {
                setSearchResults(response.data.students || []);
            }
        } catch {
            // fail silently for search
        } finally {
            setIsSearching(false);
        }
    }, []);

    const handleAddExisting = async student => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            const enrolmentsList = getEnrolments();
            const results = await Promise.all(
                enrolmentsList.map(enrol => {
                    const payload = {
                        studentProfileId: student.id,
                        role: enrol.role,
                        attendanceType,
                        // The session date being viewed, so a not-checked-in walk-in/make-up shows
                        // on THIS session's roster (even a reopened past session), not just today.
                        ...(attendanceType !== "normal" && defaultDate ? { expectedDate: defaultDate } : {}),
                        ...(attendanceType === "trial" && trialDealId ? { trialDealId } : {}),
                    };
                    if (enrol.shortCourseId) {
                        payload.shortCourseId = enrol.shortCourseId;
                    } else {
                        payload.classId = enrol.classId;
                        if (termId) payload.termId = termId;
                    }
                    return classApi.createEnrolment(payload);
                })
            );
            const allOk = results.every(r => r.ok);
            if (allOk) {
                const isMakeup = attendanceType === "makeup";
                const isWalkIn = attendanceType === "walkin";
                const isTrial = attendanceType === "trial";
                const isTemporary = isMakeup || isWalkIn || isTrial;
                let checkInFailed = false;
                if (checkInAfterEnrol) {
                    const checkInResults = await Promise.all(
                        results.map((result, i) => {
                            const enrolmentId = result.data?.enrolment?.id;
                            if (!enrolmentId) return Promise.resolve();
                            const enrol = enrolmentsList[i];
                            if (enrol.shortCourseId) {
                                return classApi.checkInShortCourse(enrol.shortCourseId, enrolmentId);
                            }
                            return classApi.checkIn(enrol.classId, enrolmentId, isMakeup, isWalkIn, defaultDate);
                        })
                    );
                    checkInFailed = checkInResults.some(r => r && !r.ok);
                }
                const flagLabel = isMakeup ? " [Make-up]" : isWalkIn ? " [Walk-in]" : isTrial ? " [Trial]" : "";
                if (checkInFailed && isTemporary) {
                    toast.warn(`${student.firstName} ${student.lastName} enrolled${flagLabel} but check-in failed. Start a session first, then check them in manually.`);
                } else {
                    const action = attendanceType === "normal"
                        ? (checkInAfterEnrol ? "enrolled and checked in" : "enrolled")
                        : (checkInAfterEnrol ? "added and checked in" : "added to the class");
                    toast.success(`${student.firstName} ${student.lastName} ${action}${flagLabel}`);
                }
                setSelectedStudent(null);
                setSearchResults([]);
                setTimeout(onStudentAdded, 400);
            } else {
                const failedResponse = results.find(r => !r.ok);
                toast.error(failedResponse?.data?.message || "Failed to enrol student");
            }
        } catch {
            toast.error("Failed to enrol student");
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleCreateNew = async () => {
        if (!firstName.trim()) {
            toast.error("First name is required");
            return;
        }
        if (!lastName.trim()) {
            toast.error("Last name is required");
            return;
        }
        if (!email.trim()) {
            toast.error("Email is required");
            return;
        }
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            const walkInPayload = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                enrolments: getEnrolments(),
                attendanceType,
                sendInvite,
                // Session date being viewed, so a not-checked-in walk-in shows on THIS session.
                ...(attendanceType !== "normal" && defaultDate ? { expectedDate: defaultDate } : {}),
            };
            if (attendanceType === "trial" && trialDealId) walkInPayload.trialDealId = trialDealId;
            const response = await classApi.createWalkIn(walkInPayload);
            if (response.ok) {
                const isMakeup = attendanceType === "makeup";
                const isWalkIn = attendanceType === "walkin";
                const createdEnrolments = response.data?.enrolments || [];
                const isTrial = attendanceType === "trial";
                const isTemporary = isMakeup || isWalkIn || isTrial;
                let checkInFailed = false;
                if (checkInAfterEnrol) {
                    const checkInResults = await Promise.all(
                        createdEnrolments.map(enrol => {
                            const enrolmentId = enrol.id;
                            if (!enrolmentId) return Promise.resolve();
                            if (enrol.shortCourseId) {
                                return classApi.checkInShortCourse(enrol.shortCourseId, enrolmentId);
                            }
                            return classApi.checkIn(enrol.classId, enrolmentId, isMakeup, isWalkIn, defaultDate);
                        })
                    );
                    checkInFailed = checkInResults.some(r => r && !r.ok);
                }
                const inviteMsg = sendInvite ? " (invite email sent)" : "";
                const flagLabel = isMakeup ? " [Make-up]" : isWalkIn ? " [Walk-in]" : isTrial ? " [Trial]" : "";
                if (checkInFailed && isTemporary) {
                    toast.warn(`${firstName} enrolled${flagLabel} but check-in failed. Start a session first, then check them in manually.${inviteMsg}`);
                } else {
                    const action = attendanceType === "normal"
                        ? (checkInAfterEnrol ? "registered and checked in" : "registered and enrolled")
                        : (checkInAfterEnrol ? "registered and checked in" : "registered and added to the class");
                    toast.success(`${firstName} ${action}${flagLabel}${inviteMsg}`);
                }
                setTimeout(onStudentAdded, 400);
            } else {
                toast.error(response.data?.message || "Failed to register student");
            }
        } catch {
            toast.error("Failed to register student");
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <div className="add-student-modal-overlay" onClick={onClose}>
            <div className="add-student-modal" onClick={e => e.stopPropagation()}>
                <div className="add-student-modal-header">
                    <h2>
                        <Icon path={mdiAccountPlus} size={1} color="#451f78" style={{ marginRight: 8 }} />
                        Add Student
                    </h2>
                    <button className="add-student-modal-close" onClick={onClose}>
                        <Icon path={mdiClose} size={0.9} />
                    </button>
                </div>

                {/* Step 1 — Who is this? */}
                <div className="add-student-who-section">
                    <label className="add-student-label">Who is this?</label>
                    <WhoToggle who={who} onChange={setWho} />
                </div>

                {who === "existing" ? (
                    <div className="add-student-search-wrapper">
                        <label className="add-student-label">Search existing students</label>
                        <div className="add-student-search-input-row">
                            <Icon path={mdiMagnify} size={0.9} color="#888" />
                            <input
                                type="text"
                                className="add-student-search-input"
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={e => handleSearch(e.target.value)}
                                autoFocus
                            />
                        </div>
                        {isSearching && <p className="add-student-searching">Searching...</p>}

                        {searchResults.length > 0 && (
                            <div className="add-student-results">
                                {searchResults.map(student => {
                                    const alreadyEnrolled = isAlreadyEnrolled(student);
                                    return (
                                    <div
                                        key={student.id}
                                        className={`add-student-result-row${selectedStudent?.id === student.id ? " selected" : ""}${alreadyEnrolled ? " enrolled" : ""}`}
                                        onClick={() => !alreadyEnrolled && setSelectedStudent(selectedStudent?.id === student.id ? null : student)}>
                                        <div className="add-student-result-info">
                                            <span className="add-student-result-name">
                                                {student.firstName} {student.lastName}
                                            </span>
                                            {student.email && (
                                                <span className="add-student-result-email">{student.email}</span>
                                            )}
                                            {alreadyEnrolled ? (
                                                <span className="add-student-result-enrolled-tag">Already enrolled</span>
                                            ) : student.source === "account" ? (
                                                <div className="class-students-account-banner class-students-account-banner--platform">
                                                    <Icon path={mdiPaw} size={0.55} color="var(--dl-color-purple-default)" />
                                                    <span>Dance Localé member</span>
                                                </div>
                                            ) : (
                                                <div className="class-students-account-banner class-students-account-banner--school-only">
                                                    <Icon path={mdiAccountCircleOutline} size={0.55} />
                                                    <span>School account only</span>
                                                </div>
                                            )}
                                        </div>
                                        {!alreadyEnrolled && (
                                            <Icon
                                                path={selectedStudent?.id === student.id ? mdiCheckCircle : mdiCircleOutline}
                                                size={0.85}
                                                color={selectedStudent?.id === student.id ? "var(--dl-color-purple-default)" : "#ccc"}
                                            />
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="add-student-form">
                        <div className="add-student-walkin-hint">
                            {attendanceType === "normal"
                                ? "This registers a new student at your school and enrols them in this class."
                                : "This registers a new student at your school and adds them to this class for this session only, not a term enrolment."}
                        </div>
                        <label className="add-student-label">First Name *</label>
                        <input
                            type="text"
                            className="add-student-input"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            placeholder="First name"
                            autoFocus
                        />

                        <label className="add-student-label">Last Name *</label>
                        <input
                            type="text"
                            className="add-student-input"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            placeholder="Last name"
                        />

                        <label className="add-student-label">
                            Email <span className="add-student-required">*</span>
                        </label>
                        <input
                            type="email"
                            className="add-student-input"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="email@example.com"
                            required
                        />

                        <CustomFormInput type="checkbox" variant="turquoise"
                            label="Send account invite"
                            checked={sendInvite}
                            onChange={e => setSendInvite(e.target.checked)}
                            disabled={!email.trim()} />
                        <p className="add-student-hint">Sends them an email invite to set up their account.</p>
                    </div>
                )}

                {/* Step 2 — Why are they here tonight? */}
                <div className="add-student-attendance-section">
                    <label className="add-student-label">Why are they here?</label>
                    <div className="add-student-attendance-options">
                        <CustomFormInput type="radio" layout="pill" variant="purple" name="attendanceType" value="normal"
                            label="Normal enrolment" checked={attendanceType === "normal"} onChange={() => setAttendanceType("normal")} />
                        <CustomFormInput type="radio" layout="pill" variant="purple" name="attendanceType" value="walkin"
                            label="Walk-in" checked={attendanceType === "walkin"} onChange={() => setAttendanceType("walkin")} />
                        <CustomFormInput type="radio" layout="pill" variant="purple" name="attendanceType" value="makeup"
                            label="Make-up" checked={attendanceType === "makeup"} onChange={() => setAttendanceType("makeup")} />
                        <CustomFormInput type="radio" layout="pill" variant="purple" name="attendanceType" value="trial"
                            label="Trial" checked={attendanceType === "trial"} onChange={() => { setAttendanceType("trial"); setTrialDealId(""); }} />
                    </div>
                    {attendanceType === "normal" && <p className="add-student-hint">Ongoing enrolment. Stays on the class roster.</p>}
                    {attendanceType === "walkin" && <p className="add-student-hint">Attending this one class only, not enrolled for the term. Added for this session so you can check them in when they arrive.</p>}
                    {attendanceType === "makeup" && <p className="add-student-hint">Attending this class to make up a missed or cancelled session, not a term enrolment. This session only.</p>}
                    {attendanceType === "trial" && (
                        <div className="add-student-trial-picker">
                            <p className="add-student-hint">Attending on a trial deal. Pick which deal applies.</p>
                            <label className="add-student-label">Trial deal *</label>
                            {trialDeals.length === 0
                                ? <p className="add-student-hint">No active trial deals found for this class level. Create one in the Promote tab first.</p>
                                : <select
                                    className="add-student-input"
                                    value={trialDealId}
                                    onChange={e => setTrialDealId(e.target.value)}
                                >
                                    <option value="">Select a trial deal</option>
                                    {trialDeals.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            }
                        </div>
                    )}
                </div>

                {/* Role + check-in apply to both paths */}
                <div className="add-student-role-section">
                    <label className="add-student-label">Role (optional)</label>
                    <RoleToggle role={role} onChange={setRole} />
                </div>
                <CustomFormInput type="checkbox" variant="turquoise"
                    label={attendanceType === "normal" ? "Check in after enrolling" : "They have arrived, check in now"}
                    checked={checkInAfterEnrol}
                    onChange={e => setCheckInAfterEnrol(e.target.checked)} />
                {attendanceType !== "normal" && !checkInAfterEnrol && (
                    <p className="add-student-hint">Leave unticked to add them as expected. They will show on the class list so you can check them in when they arrive.</p>
                )}

                {/* Confirm */}
                {who === "existing" ? (
                    selectedStudent && (
                        <button
                            className="add-student-confirm-btn"
                            onClick={() => handleAddExisting(selectedStudent)}
                            disabled={isSubmitting}>
                            {isSubmitting
                                ? (attendanceType === "normal" ? "Enrolling..." : "Adding...")
                                : `${attendanceType === "normal" ? "Enrol" : "Add"} ${selectedStudent.firstName} ${selectedStudent.lastName}`}
                        </button>
                    )
                ) : (
                    <div className="add-student-modal-actions">
                        <button
                            className="add-student-submit-btn"
                            onClick={handleCreateNew}
                            disabled={isSubmitting || !firstName.trim() || !lastName.trim()}>
                            {isSubmitting ? "Registering..." : (attendanceType === "normal" ? "Register & Enrol" : "Register & Add to Class")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddStudentModal;
